// src/components/Sidebar/useProjectStatusMap.js
import { useMemo } from "react";

const normalizeFlag = (f) =>
  typeof f === "string" ? f.trim().toLowerCase() : "";

export const truncate = (str = "", max = 10) =>
  typeof str === "string" && str.length > max ? `${str.slice(0, max)}…` : str;

/**
 * Derives, per project, whether it has aborted / on-hold / waiting
 * tasks or todos, plus the up-to-3 items shown in the hover tooltip.
 * Logic is unchanged from the original Sidebar — only relocated so it
 * can be unit-tested and memoized independently of rendering.
 */
export function useProjectStatusMap(projects) {
  return useMemo(() => {
    const map = new Map();

    projects.forEach((project) => {
      const lookup = new Map();
      (project?.category?.stageResponseDTOList || []).forEach((stage) => {
        const flagNorm = normalizeFlag(stage.stageFlag);
        (stage.stagePositionResponseDTOList || []).forEach((pos) => {
          lookup.set(pos.stagePositionId, flagNorm);
        });
      });

      let abortedCount = 0;
      let onHoldCount = 0;
      let waitingCount = 0;
      const items = [];

      (project.tasks || []).forEach((task) => {
        const taskFlag = lookup.get(task.stagePositionId);
        const todoItems = [];

        (task.todoRequestDTOList || []).forEach((todo) => {
          const todoFlag = lookup.get(todo.stagePositionId);
          const todoFlagNorm = normalizeFlag(todoFlag);

          if (todoFlagNorm === "aborted") {
            abortedCount++;
            todoItems.push({
              type: "todo",
              title: todo.toDoTitle || "(untitled)",
              flag: "aborted",
            });
          } else if (todoFlagNorm === "on hold" || todoFlagNorm === "onhold") {
            onHoldCount++;
            todoItems.push({
              type: "todo",
              title: todo.toDoTitle || "(untitled)",
              flag: "onhold",
            });
          } else if (todoFlagNorm === "waiting") {
            waitingCount++;
            todoItems.push({
              type: "todo",
              title: todo.toDoTitle || "(untitled)",
              flag: "waiting",
            });
          }
        });

        const includeTask =
          ["aborted", "on hold", "onhold", "waiting"].includes(taskFlag) ||
          todoItems.length > 0;

        if (includeTask) {
          items.push({
            type: "task",
            title: task.taskTitle || "(untitled)",
            flag:
              taskFlag === "aborted"
                ? "aborted"
                : ["on hold", "onhold"].includes(taskFlag)
                  ? "onhold"
                  : "waiting",
            todos: todoItems,
          });
        }

        if (taskFlag === "aborted") abortedCount++;
        else if (["on hold", "onhold"].includes(taskFlag)) onHoldCount++;
        else if (taskFlag === "waiting") waitingCount++;
      });

      let status = null;
      if (abortedCount > 0) {
        status = { type: "aborted", abortedCount, onHoldCount, waitingCount, items };
      } else if (onHoldCount > 0) {
        status = { type: "onHold", abortedCount, onHoldCount, waitingCount, items };
      } else if (waitingCount > 0) {
        status = { type: "waiting", abortedCount, onHoldCount, waitingCount, items };
      }

      map.set(project.projectId, status);
    });

    return map;
  }, [projects]);
}
